/**
 * Der **Klebestreifen** — welcher, wie schräg, wie groß.
 *
 * Bewusst ohne React-Bezug, wie die `utils/` nebenan: Die Rechnung soll ohne
 * gerenderten Canvas prüfbar sein.
 *
 * Siehe „Klebestreifen" in CONTEXT.md und
 * `.claude/tasks/canvas-klebestreifen.md`.
 */

import type { CanvasNote } from '@/src/renderer/lib/db/models'
import { WASHI_1_SVG, WASHI_2_SVG } from './canvasTapeArt'

/** Die Schlüssel, wie sie in `canvasNotes.tape` stehen. */
export const CANVAS_TAPE_KEYS = ['washi-1', 'washi-2'] as const

export type CanvasTapeKey = (typeof CANVAS_TAPE_KEYS)[number]

/**
 * **Wo** der Streifen liegt.
 *
 * `'corner'` — schräg über der linken oberen Ecke, mit Überstand nach außen.
 * `'top'` — waagerecht, mittig auf der OBERKANTE: halb darüber, halb auf dem
 * Papier, wie ein Streifen, der ein Blatt von oben an die Wand hält.
 *
 * Beide sind dieselbe Verankerung, nur mit anderen Werten (siehe
 * `--tape-mid-x`/`--tape-mid-y` in `CanvasTape.scss`) — die Lage ist kein
 * zweiter Mechanismus, sondern ein zweiter Ort.
 */
export const CANVAS_TAPE_SPOTS = ['corner', 'top'] as const

export type CanvasTapeSpot = (typeof CANVAS_TAPE_SPOTS)[number]

/**
 * Die vier Neigungen, in **7°-Stufen** — **was der Würfel zieht.**
 *
 * **Nur eine Richtung, keine Minuswerte.** Bei umgekehrter Neigung kippt das
 * freie Ende des Streifens nach unten ins Textfeld; alle vier neigen sich
 * deshalb gleichsinnig, so wie die beiden im Entwurf (14° und 28° — zwei der
 * vier Stufen, nicht der ganze Vorrat).
 *
 * **Was die HAND erreicht, steht in `canvasTapeAngles`** und geht weiter: in
 * der Ecke zwei Stufen darüber hinaus, auf der Oberkante nach beiden Seiten.
 * Diese Liste hier bleibt der Vorrat des Anklebens — sonst klebte plötzlich
 * schief an, was noch nie schief angeklebt hat.
 */
export const CANVAS_TAPE_ANGLES = [7, 14, 21, 28] as const

/**
 * **Die Schrittweite des Rasters — 7°.**
 *
 * Sie gehört dem Streifen, aber nicht nur ihm: **Zettel** und **Memos**
 * übernehmen sie (siehe `canvasRotation.ts`). Ein Canvas, auf dem zwei Dinge in
 * verschiedenen Stufen rasten, hätte zwei Sprachen für dieselbe Sache.
 */
export const CANVAS_TAPE_ANGLE_STEP = 7

/**
 * **Die steilste Stufe, die der Streifen erreicht** — 42°, in der Ecke und nur
 * von Hand (siehe `CANVAS_TAPE_ANGLES_WIDE`).
 *
 * Auch sie übernehmen **Zettel** und **Memos**, dort nach BEIDEN Seiten
 * (−42° bis 42°). Warum ausgerechnet 42: Weiter oben steht es — bei 49° wäre
 * es knapp, bei 56° rutschte die Zettelecke unter dem Streifen heraus. Die
 * Grenze ist am Papier nachgerechnet, und wo es eine begründete Grenze schon
 * gibt, braucht es keine zweite erfundene daneben.
 */
export const CANVAS_TAPE_ANGLE_MAX = 42

/**
 * Die beiden Stufen, die es NUR beim Ziehen gibt — in der Ecke, jenseits der
 * gewürfelten vier.
 *
 * Das 7°-Raster geht weiter, und die Zettelecke bleibt bedeckt: Nachgerechnet
 * gegen `--tape-mid` (28/10) und die Maße aus Figma liegt sie bei 42° noch
 * 11.3px innerhalb des schmaleren Streifens. 49° wäre knapp, bei 56° rutscht
 * sie unter `washi-2` heraus — dort hielte der Streifen die Ecke nicht mehr,
 * und das ist sein Zweck.
 */
const CANVAS_TAPE_ANGLES_WIDE = [35, 42] as const

/**
 * Die Rasten, durch die das **Ziehen** geht — je Lage verschieden, und in
 * beiden Fällen weiter als das, was der Würfel zieht.
 *
 * **Die Hand darf, was der Würfel nicht darf:** Angeklebt wird aus
 * `CANVAS_TAPE_ANGLES` (7–28, eine Richtung). Wer zieht, kommt darüber hinaus.
 *
 * *Ecke:* dieselbe Richtung, aber zwei Stufen weiter (35°, 42°). Die eine
 * Richtung bleibt: Andersherum kippte das freie Ende nach unten ins Textfeld.
 *
 * *Oberkante:* nach BEIDEN Seiten, mit der Waagerechten in der Mitte. Der Grund
 * für „nur eine Richtung" ist die Ecke — ein Streifen, der mittig auf der Kante
 * sitzt, hebt auf der einen Seite ab und senkt sich auf der anderen, ganz
 * gleich herum. Und die 0 gehört dorthin, weil sie die Grundstellung ist: Ohne
 * sie wäre der Zug oben eine Einbahnstraße.
 */
export function canvasTapeAngles(spot: CanvasTapeSpot): number[] {
  if (spot === 'top') {
    return [...[...CANVAS_TAPE_ANGLES].reverse().map(a => -a), 0, ...CANVAS_TAPE_ANGLES]
  }
  return [...CANVAS_TAPE_ANGLES, ...CANVAS_TAPE_ANGLES_WIDE]
}

/**
 * **Eine Raste weiter** — für die Pfeiltasten am Streifen.
 *
 * `dir` ist `+1` (die nächste Stufe) oder `−1` (die vorige). **An den Enden ist
 * Schluss**, kein Umlauf: Wer über 42° hinaus will, soll nicht bei 7° wieder
 * herauskommen.
 *
 * Bis September 2026 kam auch der ZUG hier durch (`angleAfterDrag`, 22
 * Bildschirmpixel je Raste, senkrecht). Seit die Neigung im Kreis um die Mitte
 * gestellt wird (CONTEXT.md, **Neigung**), hat der Zug keine „Strecke je Raste"
 * mehr — er hat einen Winkel. Geblieben ist die Tastatur, und für sie ist
 * „eine Raste weiter" die ganze Frage.
 *
 * **Pfeil runter ist die nächste Stufe.** Das ist jetzt eine Verabredung und
 * kein Abbild des Zugs mehr: Wer den Streifen im Kreis führt, greift ihn
 * irgendwo auf seiner Fläche — links vom Drehpunkt bedeutet „nach unten" das
 * Gegenteil von rechts davon. Eine Taste kann das nicht spiegeln, also nimmt
 * sie die Reihenfolge der Liste.
 */
export function stepCanvasTapeAngle(spot: CanvasTapeSpot, angle: number, dir: 1 | -1): number {
  const rasten = canvasTapeAngles(spot)
  const von = Math.max(0, rasten.indexOf(nearestCanvasTapeAngle(spot, angle)))
  return rasten[Math.min(rasten.length - 1, Math.max(0, von + dir))]!
}

/**
 * Der Winkel, auf die nächste Raste DIESER Lage gezogen.
 *
 * Gebraucht beim **Lagenwechsel**: Ein von Hand gesetzter Winkel fährt mit,
 * aber die neue Lage kennt vielleicht andere Rasten — waagerecht gibt es in
 * der Ecke nicht.
 */
export function nearestCanvasTapeAngle(spot: CanvasTapeSpot, angle: number): number {
  const rasten = canvasTapeAngles(spot)
  return rasten.reduce((beste, raste) =>
    Math.abs(raste - angle) < Math.abs(beste - angle) ? raste : beste, rasten[0]!)
}

/**
 * Wie lange sich der Streifen beim **Ablösen** zurückzieht.
 *
 * Dieselbe Zahl steht in `CanvasTape.scss` als Dauer der Animation; ein Test
 * hält die beiden zusammen (`canvasTapes.test.ts`). CSS kann sie nicht von hier
 * lesen und JS nicht aus der SCSS — deshalb steht sie zweimal, aber nur einmal
 * ohne Aufsicht.
 *
 * **Sie ist NICHT die Wartezeit vor dem Schreiben.** Wann die Zeile fällt,
 * meldet der Streifen selbst (`CanvasTape`, `onPeeled`): Eine Uhr startete im
 * Klick, die Animation aber erst, wenn der Stil steht — einen Frame später —,
 * und der Streifen verschwand kurz vor dem Ende seiner Bewegung. Der Canvas
 * benutzt die Zahl nur noch als großzügiges Sicherheitsnetz, falls die Meldung
 * ausbleibt.
 */
export const CANVAS_TAPE_PEEL_MS = 150

/**
 * Wie lange das **Aufrollen** beim Ankleben dauert — muss zu
 * `canvas-tape-roll-on` in `CanvasTape.scss` passen.
 *
 * `CanvasTape` braucht die Zahl, weil es die Klasse nach dem Lauf wieder
 * abnimmt: Bliebe sie stehen, rollte der Streifen bei jedem Tabwechsel neu auf
 * (ein inaktiver Tab steht auf `display: none`, und was daraus zurückkommt,
 * startet seine Animationen von vorn).
 */
export const CANVAS_TAPE_ROLL_ON_MS = 320

/** Ein Streifen, wie ihn die Darstellung braucht: Bild und Maße. */
export interface CanvasTapeShape {
  /** Das Bild als `data:`-URL, fertig für `background-image`. */
  url: string
  /** Breite im unrotierten Zustand. */
  width: number
  /** Höhe im unrotierten Zustand. */
  height: number
}

/**
 * Ein SVG als `data:`-URL.
 *
 * `encodeURIComponent` und nicht Base64: Der Text bleibt lesbar, das Ergebnis
 * ist kürzer, und ein `#` im Bild (Farbwerte!) bricht die URL sonst — es
 * beendete den Pfad und alles danach wäre ein Fragment.
 */
const toDataUrl = (svg: string): string =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

/** Die Streifen, nach Schlüssel. */
export const CANVAS_TAPE_SHAPES: Record<CanvasTapeKey, CanvasTapeShape> = {
  'washi-1': { url: toDataUrl(WASHI_1_SVG), width: 127.785, height: 35.7872 },
  'washi-2': { url: toDataUrl(WASHI_2_SVG), width: 111.812, height: 32.0444 },
}

/** Wie ein Streifen auf einem Zettel liegt — alles, was die Darstellung braucht. */
export interface CanvasTapeLook {
  key: CanvasTapeKey
  spot: CanvasTapeSpot
  /**
   * Grad, gegen den Uhrzeigersinn. Auf der Oberkante 0 — es sei denn, die Hand
   * hat dort gezogen (siehe `byHand`).
   */
  angle: number
  /**
   * Ob der Winkel **gezogen** wurde statt gewürfelt (CONTEXT.md, **Neigung
   * ziehen**).
   *
   * Es entscheidet EINE Frage: ob **Streifen wechseln** den Winkel mitwürfeln
   * darf. Wer selbst gezogen hat, hat eine Absicht, und ein Würfel überschreibt
   * sie nicht — zurück zum Zufall führt nur Ablösen und neu ankleben.
   *
   * Warum das überhaupt in der Zeile steht: ADR 0013.
   */
  byHand: boolean
}

/**
 * Der vierte Teil der Zeile: „diesen Winkel hat die Hand gesetzt".
 *
 * **Ein vierter Teil und kein Zeichen am Winkel** (`21!`): Die Zeile geht in
 * die Cloud und kann von einer älteren App-Version gelesen werden. Die
 * ignoriert einen vierten Teil und liest den Winkel richtig — `Number('21!')`
 * gäbe `NaN` und würfe den Streifen auf die erste Raste zurück. Fehlt der Teil,
 * heißt das „gewürfelt"; so lesen sich alle Zeilen, die es schon gibt.
 */
const HAND = 'hand'

/**
 * **Alle Erscheinungen, die es für diese Art gibt.**
 *
 * Zehn für den **Notizen**: acht schräge in der Ecke (zwei Muster mal vier
 * Stufen) und zwei gerade oben — waagerecht ist waagerecht, dort gibt es keine
 * Neigung. Acht für die **Notiz**: Sie bekommt nie die Oberkante, dort steht
 * ihr **Titel**.
 *
 * Als Liste und nicht als drei Würfe: Nur so lässt sich einer davon AUSNEHMEN
 * (siehe `drawCanvasTape`), ohne im Kreis zu würfeln, bis etwas anderes fällt.
 */
export function canvasTapeStock(kind: CanvasNote['kind']): string[] {
  const spots: CanvasTapeSpot[] = kind === 'note' ? [...CANVAS_TAPE_SPOTS] : ['corner']
  return spots.flatMap(spot =>
    CANVAS_TAPE_KEYS.flatMap(key =>
      (spot === 'top' ? [0] : CANVAS_TAPE_ANGLES).map(angle => `${key}:${spot}:${angle}`)))
}

/**
 * **Einmal ziehen, beim Ankleben — und nie dasselbe zweimal hintereinander.**
 *
 * Wer einen Zettel ablöst und neu anklebt, greift in die Schachtel und zieht ein
 * anderes Stück. **Anderes** heißt hier wörtlich: `avoid` fällt aus dem Vorrat,
 * bevor gezogen wird. Sonst käme mit einer Wahrscheinlichkeit von eins zu zehn
 * derselbe Streifen zurück, und das Durchwechseln — das einzige, was es hier an
 * Auswahl gibt — hakte ohne Grund. **Es braucht dafür keinen eigenen Knopf:**
 * Ablösen und neu ankleben IST der Wähler.
 *
 * **Das Ergebnis wandert in die Zeile**, statt bei jedem Zeichnen neu zu fallen:
 * Ein Würfel im Rendern fiele bei JEDEM Neuaufbau des Nodes — beim Verschieben,
 * beim Tippen, beim Tab-Wechsel —, und der Streifen flackerte durch alle zehn
 * Erscheinungen. (Bis 2026-09-06 wurde er aus der Kennung gerechnet. Das war
 * stabil, aber ein Zettel behielt sein Stück für immer, auch nach dem Ablösen.)
 *
 * Das Feld bleibt EINES: `muster:lage:winkel`, und die Anwesenheit dieses
 * Strings IST weiter die Sperre.
 *
 * @param avoid die zuletzt gezogene Erscheinung — sie kommt nicht wieder dran
 */
export function drawCanvasTape(kind: CanvasNote['kind'], avoid?: string | null): string {
  const vorrat = canvasTapeStock(kind)
  const uebrig = vorrat.filter(erscheinung => erscheinung !== avoid)
  // `uebrig` ist nie leer: Der Vorrat hat acht Einträge, ausgenommen wird einer.
  return uebrig[Math.floor(Math.random() * uebrig.length)]!
}

/**
 * **Streifen wechseln** — ein anderes Stück, aber nicht gegen die Hand.
 *
 * Solange nur gewürfelt wurde, ist es derselbe Griff wie eh und je: ein
 * anderes ganzes Stück aus der Schachtel (`drawCanvasTape`), nie dasselbe
 * zweimal.
 *
 * **Hat die Hand den Winkel gezogen, gehört er ihr.** Gewechselt werden dann
 * nur Muster und Lage; die Neigung fährt mit, samt Merkmal — ohne das würfelte
 * der nächste Druck sie doch noch weg. Kennt die neue Lage den Winkel nicht
 * (waagerecht gibt es in der Ecke nicht), rückt er auf ihre nächste Raste.
 *
 * **Der Preis steht in ADR 0013:** Bei einer **Notiz** — sie bekommt nie die
 * Oberkante — bleibt dann genau EIN anderes Paar übrig, und der Knopf wird zum
 * Umschalter zwischen den zwei Mustern. Das ist die Seite der Abwägung, die wir
 * gewählt haben: lieber ein schmalerer Knopf als ein Würfel, der die Absicht
 * der Hand überschreibt.
 *
 * @param current was GERADE draufliegt — beim Wechseln ist das die Frage, nicht
 *   „was habe ich zuletzt gezogen"
 */
export function retapeCanvasTape(kind: CanvasNote['kind'], current: string): string {
  const look = readCanvasTape(current)
  if (!look?.byHand) return drawCanvasTape(kind, current)

  const spots: CanvasTapeSpot[] = kind === 'note' ? [...CANVAS_TAPE_SPOTS] : ['corner']
  const paare = spots.flatMap(spot => CANVAS_TAPE_KEYS.map(key => ({ key, spot })))
  // Das aktuelle Paar fällt heraus: Sonst täte der Knopf mit einer gewissen
  // Wahrscheinlichkeit gar nichts, denn der Winkel bleibt ja ohnehin.
  const uebrig = paare.filter(p => !(p.key === look.key && p.spot === look.spot))
  // Nie leer: Es gibt mindestens zwei Muster, ausgenommen wird ein Paar.
  const gezogen = uebrig[Math.floor(Math.random() * uebrig.length)]!
  return writeCanvasTape({
    ...gezogen,
    angle: nearestCanvasTapeAngle(gezogen.spot, look.angle),
    byHand: true,
  })
}

/**
 * Was in der Zeile steht, für die Darstellung gelesen.
 *
 * **Nachsichtig, mit Absicht:** Ein Zettel, der laut Zeile klebt, MUSS einen
 * Streifen zeigen — sonst hätte er keinen Griff mehr, und man käme nie wieder an
 * ihn heran. Ein unbekanntes Muster fällt deshalb auf das erste zurück, eine
 * unbekannte Lage auf die Ecke, ein unbekannter Winkel auf die erste Stufe.
 *
 * Eine Zeile aus der ersten Fassung trägt nur den Musternamen (`washi-1`); sie
 * liest sich damit als schräger Streifen in der Ecke.
 */
export function readCanvasTape(tape: string | null): CanvasTapeLook | null {
  if (!tape) return null
  const [key, spot, angle, hand] = tape.split(':')
  const gelesen = Number(angle)
  const lage = CANVAS_TAPE_SPOTS.includes(spot as CanvasTapeSpot) ? (spot as CanvasTapeSpot) : 'corner'
  const rasten = canvasTapeAngles(lage)
  return {
    key: CANVAS_TAPE_KEYS.includes(key as CanvasTapeKey) ? (key as CanvasTapeKey) : CANVAS_TAPE_KEYS[0],
    spot: lage,
    // Die Rasten der LAGE, nicht die vier festen: Auf der Oberkante ist die
    // Waagerechte eine davon, in der Ecke nicht.
    angle: rasten.includes(gelesen) ? gelesen : rasten[0]!,
    byHand: hand === HAND,
  }
}

/**
 * **Welches STÜCK hier liegt** — Muster und Lage, ohne die Neigung.
 *
 * Das ist die Kennung, an der der Streifen im Baum hängt (`key` in
 * `NoteNode`/`MemoNode`): Ein neues Stück klebt sich an, dasselbe
 * Stück gedreht nicht. Stünde der ganze Zeileninhalt im `key`, nähme jede neue
 * Stufe den Streifen aus dem Baum und setzte ihn neu hinein — und beim
 * Erscheinen läuft das **Ankleben**. Gemeldet 2026-09-06: Am Ende eines Zugs
 * rollte er sich noch einmal auf.
 */
export function canvasTapeIdentity(tape: string | null): string | null {
  const look = readCanvasTape(tape)
  return look && `${look.key}:${look.spot}`
}

/**
 * Die Gegenrichtung zu `readCanvasTape` — damit das Format an EINER Stelle
 * entsteht und nicht an jeder, die einen Streifen schreibt.
 */
export function writeCanvasTape(look: CanvasTapeLook): string {
  return `${look.key}:${look.spot}:${look.angle}${look.byHand ? `:${HAND}` : ''}`
}

/** Bild und Maße zu einer gelesenen Erscheinung. */
export function getCanvasTape(look: CanvasTapeLook | null): CanvasTapeShape | null {
  return look ? CANVAS_TAPE_SHAPES[look.key] : null
}
